import * as THREE from 'three'
import type { FloorLayout, StorefrontSlot } from './types'
import { FASCIA_HEIGHT, PIER_WIDTH, SLOT_WIDTH, SOFFIT_HEIGHT, STOREFRONT_HEIGHT } from './mallLayout'
import { MALL, makeFasciaSignTexture, makeShopInteriorTexture } from './mallTextures'
import type { MallFinishes } from './mallArchitecture'

/**
 * One tenant bay, built to the shape real mall tenant-design criteria produce:
 *
 *   ┌── neutral pier ──┐                        ┌── neutral pier ──┐
 *   │  ╔════════════ illuminated fascia sign ════════════╗         │   4.75 m
 *   │  ╚═════════════════════════════════════════════════╝         │   3.60 m  ← lease line
 *   │  │  lightbox  │        glazing        │ door │      │        │
 *   │  └────────────── stall riser ──────────────────────┘         │   0.00 m
 *
 * The pier is landlord-owned and identical on every bay; everything inside it is
 * the tenant's. That contrast — a repeating neutral frame around varied tenant
 * content — is what makes a mall run read as a mall run rather than a corridor.
 */

const BAY_CLEAR = SLOT_WIDTH - PIER_WIDTH * 2 // glazed opening between piers
const PIER_DEPTH = 0.45
const PIER_FACE = PIER_DEPTH / 2 // local +Z of the pier's front face
const RECESS_DEPTH = 2.8
const RISER_HEIGHT = 0.26

/** Geometry and materials shared by every bay on a floor — built once per floor. */
export class StorefrontKit {
  readonly interiorTex: THREE.CanvasTexture
  readonly accentHex: string
  private accent: THREE.Color

  readonly pierGeo = new THREE.BoxGeometry(PIER_WIDTH, SOFFIT_HEIGHT, PIER_DEPTH)
  readonly pierMat: THREE.MeshStandardMaterial
  readonly pierBaseGeo = new THREE.BoxGeometry(PIER_WIDTH + 0.06, 0.34, PIER_DEPTH + 0.06)
  readonly metalMat: THREE.MeshStandardMaterial

  readonly fasciaGeo = new THREE.BoxGeometry(BAY_CLEAR, FASCIA_HEIGHT, 0.36)
  readonly fasciaMat: THREE.MeshStandardMaterial

  readonly glassMat: THREE.MeshStandardMaterial
  readonly glassGeo = new THREE.PlaneGeometry(BAY_CLEAR, STOREFRONT_HEIGHT - RISER_HEIGHT)

  readonly interiorMat: THREE.MeshStandardMaterial
  readonly recessSideMat: THREE.MeshStandardMaterial
  readonly recessCeilMat: THREE.MeshBasicMaterial
  readonly recessFloorMat: THREE.MeshStandardMaterial

  readonly riserMat: THREE.MeshStandardMaterial
  readonly thresholdMat: THREE.MeshBasicMaterial
  readonly washMat: THREE.MeshBasicMaterial
  readonly reflectionMat: THREE.MeshBasicMaterial
  readonly frameMat: THREE.MeshStandardMaterial
  readonly revealMat: THREE.MeshBasicMaterial

  private extra: Array<{ dispose: () => void }> = []

  constructor(floor: FloorLayout, fin: MallFinishes) {
    this.accentHex = floor.color
    this.accent = new THREE.Color(floor.color)
    this.interiorTex = makeShopInteriorTexture(floor.color)

    this.pierMat = new THREE.MeshStandardMaterial({
      map: fin.tiled(fin.limestone, 0.3, 3),
      roughness: 0.7,
      metalness: 0.04,
      envMapIntensity: 0.7,
    })
    this.metalMat = new THREE.MeshStandardMaterial({
      color: MALL.bronze, roughness: 0.35, metalness: 0.85, envMapIntensity: 1.2,
    })
    this.fasciaMat = new THREE.MeshStandardMaterial({
      map: fin.tiled(fin.limestone, 3, 0.7),
      roughness: 0.72,
      metalness: 0.04,
      envMapIntensity: 0.7,
    })

    // Tinted, highly reflective glazing. The environment map does the heavy
    // lifting here: without it the glass reads as flat grey film.
    this.glassMat = new THREE.MeshStandardMaterial({
      color: MALL.glassTint,
      transparent: true,
      opacity: 0.13,
      roughness: 0.04,
      metalness: 0.95,
      envMapIntensity: 1.5,
      depthWrite: false,
      side: THREE.DoubleSide,
    })

    this.interiorMat = new THREE.MeshStandardMaterial({ map: this.interiorTex, roughness: 0.9, metalness: 0 })
    this.recessSideMat = new THREE.MeshStandardMaterial({ color: '#e6ddcf', roughness: 0.85, metalness: 0, side: THREE.DoubleSide })
    this.recessCeilMat = new THREE.MeshBasicMaterial({ color: '#fff8ec', side: THREE.DoubleSide })
    this.recessFloorMat = new THREE.MeshStandardMaterial({ color: '#d6cbba', roughness: 0.3, metalness: 0.05, side: THREE.DoubleSide })

    this.riserMat = new THREE.MeshStandardMaterial({ color: MALL.granite, roughness: 0.2, metalness: 0.25, envMapIntensity: 1.0 })
    this.frameMat = new THREE.MeshStandardMaterial({ color: MALL.bronzeLight, roughness: 0.3, metalness: 0.9, envMapIntensity: 1.3 })
    this.revealMat = new THREE.MeshBasicMaterial({ color: '#3a332c' })

    this.thresholdMat = new THREE.MeshBasicMaterial({ color: this.accent, transparent: true, opacity: 0.5 })
    this.washMat = new THREE.MeshBasicMaterial({
      map: fin.falloff, color: MALL.warmLight, transparent: true, opacity: 0.26,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })
    // Shopfront light smeared into the polished floor in front of the bay
    this.reflectionMat = new THREE.MeshBasicMaterial({
      map: fin.falloff, color: '#ffeed4', transparent: true, opacity: 0.17,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })
  }

  track(d: { dispose: () => void }) {
    this.extra.push(d)
  }

  dispose() {
    this.interiorTex.dispose()
    this.extra.forEach((d) => d.dispose())
    this.extra.length = 0
    ;[this.pierGeo, this.pierBaseGeo, this.fasciaGeo, this.glassGeo].forEach((g) => g.dispose())
    ;[this.pierMat, this.metalMat, this.fasciaMat, this.glassMat, this.interiorMat, this.recessSideMat,
      this.recessCeilMat, this.recessFloorMat, this.riserMat, this.thresholdMat, this.washMat,
      this.reflectionMat, this.frameMat, this.revealMat].forEach((m) => m.dispose())
  }
}

export interface BuiltStorefront {
  group: THREE.Group
  /** Material whose map is swapped in when the shop's real poster loads. */
  posterMat: THREE.MeshBasicMaterial
}

export function buildStorefront(slot: StorefrontSlot, kit: StorefrontKit, placeholder: THREE.Texture): BuiltStorefront {
  const g = new THREE.Group()
  g.position.set(slot.position.x, slot.position.y, slot.position.z)
  g.rotation.y = slot.rotationY

  buildRecess(g, kit)
  const posterMat = buildLightbox(g, kit, placeholder)
  buildGlazing(g, kit)
  buildPiers(g, kit)
  buildFascia(g, kit, slot)
  buildFloorTreatment(g, kit)

  return { group: g, posterMat }
}

// ── The shop behind the glass ──────────────────────────────────────────

/** A shallow lit box, so the shopfront has real depth rather than a painted plane. */
function buildRecess(g: THREE.Group, kit: StorefrontKit) {
  const back = new THREE.Mesh(new THREE.PlaneGeometry(BAY_CLEAR, STOREFRONT_HEIGHT), kit.interiorMat)
  back.position.set(0, STOREFRONT_HEIGHT / 2, -RECESS_DEPTH)
  g.add(back)

  const sideGeo = new THREE.PlaneGeometry(RECESS_DEPTH, STOREFRONT_HEIGHT)
  for (const s of [-1, 1]) {
    const side = new THREE.Mesh(sideGeo, kit.recessSideMat)
    side.rotation.y = s > 0 ? -Math.PI / 2 : Math.PI / 2
    side.position.set(s * (BAY_CLEAR / 2), STOREFRONT_HEIGHT / 2, -RECESS_DEPTH / 2)
    g.add(side)
  }

  const capGeo = new THREE.PlaneGeometry(BAY_CLEAR, RECESS_DEPTH)
  const ceil = new THREE.Mesh(capGeo, kit.recessCeilMat)
  ceil.rotation.x = Math.PI / 2
  ceil.position.set(0, STOREFRONT_HEIGHT - 0.02, -RECESS_DEPTH / 2)
  g.add(ceil)

  const floor = new THREE.Mesh(capGeo, kit.recessFloorMat)
  floor.rotation.x = -Math.PI / 2
  floor.position.set(0, 0.02, -RECESS_DEPTH / 2)
  g.add(floor)
}

/**
 * The tenant's campaign lightbox, standing just behind the glass. Unlit material
 * on purpose — a backlit display is a light source, not a lit surface, and this
 * is what carries the shop's poster.
 */
function buildLightbox(g: THREE.Group, kit: StorefrontKit, placeholder: THREE.Texture): THREE.MeshBasicMaterial {
  const W = 2.5
  const H = 2.1
  const cx = -1.28
  const cy = 1.92
  const z = -0.55

  const surround = new THREE.Mesh(
    new THREE.PlaneGeometry(W + 0.12, H + 0.12),
    new THREE.MeshBasicMaterial({ color: '#fffaf0' })
  )
  surround.position.set(cx, cy, z - 0.01)
  g.add(surround)

  const posterMat = new THREE.MeshBasicMaterial({ map: placeholder })
  const poster = new THREE.Mesh(new THREE.PlaneGeometry(W, H), posterMat)
  poster.position.set(cx, cy, z)
  g.add(poster)

  // Bronze frame edges
  const edgeH = new THREE.PlaneGeometry(W + 0.14, 0.05)
  const edgeV = new THREE.PlaneGeometry(0.05, H + 0.14)
  for (const dy of [-1, 1]) {
    const e = new THREE.Mesh(edgeH, kit.frameMat)
    e.position.set(cx, cy + dy * (H / 2 + 0.06), z + 0.005)
    g.add(e)
  }
  for (const dx of [-1, 1]) {
    const e = new THREE.Mesh(edgeV, kit.frameMat)
    e.position.set(cx + dx * (W / 2 + 0.06), cy, z + 0.005)
    g.add(e)
  }
  kit.track(edgeH)
  kit.track(edgeV)

  // Glow the lightbox throws onto the glass and the recess floor
  const bleed = new THREE.Mesh(
    new THREE.PlaneGeometry(W + 1.0, H + 0.9),
    new THREE.MeshBasicMaterial({
      color: '#ffffff', transparent: true, opacity: 0.07,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })
  )
  bleed.position.set(cx, cy, z + 0.06)
  g.add(bleed)

  return posterMat
}

// ── The glazing line ───────────────────────────────────────────────────

function buildGlazing(g: THREE.Group, kit: StorefrontKit) {
  const glassY = RISER_HEIGHT + (STOREFRONT_HEIGHT - RISER_HEIGHT) / 2
  const glass = new THREE.Mesh(kit.glassGeo, kit.glassMat)
  glass.position.set(0, glassY, 0)
  glass.renderOrder = 2
  g.add(glass)

  // Stall riser under the glazing — takes the scuffs in a real shop
  const riser = new THREE.Mesh(new THREE.BoxGeometry(BAY_CLEAR, RISER_HEIGHT, 0.14), kit.riserMat)
  riser.position.set(0, RISER_HEIGHT / 2, 0)
  g.add(riser)

  // Head rail, and vertical mullions splitting display / door / display.
  // The criteria forbid a flat unbroken shopfront, so the bays are uneven.
  const railGeo = new THREE.BoxGeometry(BAY_CLEAR, 0.1, 0.16)
  const head = new THREE.Mesh(railGeo, kit.frameMat)
  head.position.set(0, STOREFRONT_HEIGHT - 0.05, 0.01)
  g.add(head)
  kit.track(railGeo)

  const mullionGeo = new THREE.BoxGeometry(0.08, STOREFRONT_HEIGHT - RISER_HEIGHT, 0.16)
  for (const x of [-BAY_CLEAR / 2 + 0.04, -0.05, 1.45, BAY_CLEAR / 2 - 0.04]) {
    const m = new THREE.Mesh(mullionGeo, kit.frameMat)
    m.position.set(x, glassY, 0.01)
    g.add(m)
  }
  kit.track(mullionGeo)

  // Entrance: a pair of glass leaves between the middle mullions
  const doorW = 1.5
  const doorX = 0.7
  const doorFrame = new THREE.Mesh(
    new THREE.BoxGeometry(doorW, 0.07, 0.14),
    kit.frameMat
  )
  doorFrame.position.set(doorX, 2.35, 0.02)
  g.add(doorFrame)
  const pushBar = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.9, 8), kit.frameMat)
  pushBar.position.set(doorX, 1.05, 0.05)
  g.add(pushBar)
  const doorStile = new THREE.Mesh(new THREE.BoxGeometry(0.06, 2.35 - RISER_HEIGHT, 0.12), kit.frameMat)
  doorStile.position.set(doorX, (2.35 + RISER_HEIGHT) / 2, 0.02)
  g.add(doorStile)

  // Raking highlight on the glass — sells it as a reflective surface
  const sheen = new THREE.Mesh(
    new THREE.PlaneGeometry(BAY_CLEAR * 0.42, STOREFRONT_HEIGHT - RISER_HEIGHT),
    new THREE.MeshBasicMaterial({
      color: '#eaf4ff', transparent: true, opacity: 0.06,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })
  )
  sheen.position.set(-1.1, glassY, 0.03)
  sheen.renderOrder = 3
  g.add(sheen)
}

// ── Landlord frame: piers and fascia ───────────────────────────────────

function buildPiers(g: THREE.Group, kit: StorefrontKit) {
  for (const s of [-1, 1]) {
    const x = s * (SLOT_WIDTH / 2 - PIER_WIDTH / 2)
    const pier = new THREE.Mesh(kit.pierGeo, kit.pierMat)
    pier.position.set(x, SOFFIT_HEIGHT / 2, 0)
    g.add(pier)

    const base = new THREE.Mesh(kit.pierBaseGeo, kit.metalMat)
    base.position.set(x, 0.17, 0)
    g.add(base)
  }
}

function buildFascia(g: THREE.Group, kit: StorefrontKit, slot: StorefrontSlot) {
  const y = STOREFRONT_HEIGHT + FASCIA_HEIGHT / 2

  const fascia = new THREE.Mesh(kit.fasciaGeo, kit.fasciaMat)
  fascia.position.set(0, y, PIER_FACE - 0.18)
  g.add(fascia)

  // Shadow reveal under the fascia. Without this the band and the pier are the
  // same stone at the same brightness, and the bulkhead stops reading at all.
  const revealGeo = new THREE.BoxGeometry(BAY_CLEAR, 0.06, 0.1)
  const reveal = new THREE.Mesh(revealGeo, kit.revealMat)
  reveal.position.set(0, STOREFRONT_HEIGHT + 0.03, PIER_FACE - 0.02)
  g.add(reveal)
  kit.track(revealGeo)

  const signTex = makeFasciaSignTexture(slot.shop.name, kit.accentHex)
  kit.track(signTex)
  const signGeo = new THREE.PlaneGeometry(BAY_CLEAR * 0.9, FASCIA_HEIGHT * 0.84)
  const sign = new THREE.Mesh(
    signGeo,
    new THREE.MeshBasicMaterial({ map: signTex, transparent: true })
  )
  sign.position.set(0, y + 0.04, PIER_FACE + 0.01)
  g.add(sign)
  kit.track(signGeo)

  // Wash from the fascia's own trough light, spilling down over the glazing
  const wash = new THREE.Mesh(new THREE.PlaneGeometry(BAY_CLEAR, 1.7), kit.washMat)
  wash.position.set(0, STOREFRONT_HEIGHT - 0.85, PIER_FACE - 0.02)
  g.add(wash)
}

// ── Floor treatment in front of the bay ────────────────────────────────

function buildFloorTreatment(g: THREE.Group, kit: StorefrontKit) {
  // Entry threshold in the wing's wayfinding color
  const threshold = new THREE.Mesh(new THREE.PlaneGeometry(BAY_CLEAR * 0.9, 0.14), kit.thresholdMat)
  threshold.rotation.x = -Math.PI / 2
  threshold.position.set(0, 0.006, PIER_FACE + 0.5)
  g.add(threshold)

  // Reflection of the lit shopfront in the polished terrazzo
  const reflection = new THREE.Mesh(new THREE.PlaneGeometry(SLOT_WIDTH, 3.4), kit.reflectionMat)
  reflection.rotation.x = -Math.PI / 2
  reflection.rotation.z = Math.PI // falloff fades away from the shopfront
  reflection.position.set(0, 0.03, PIER_FACE + 1.7)
  g.add(reflection)
}
